import { apiClient } from '@/api/api'
import type {
  AddCapsuleItemsBody,
  AddCapsuleItemsResponse,
  CapsuleInventoryResponse,
  CapsuleItemRemovalImpact,
  CapsuleOutfitsResponse,
  CreateCapsuleBody,
  CreateCapsuleResponse,
  DeleteCapsuleItemResponse,
  DeleteCapsuleResponse,
  DetachOutfitsResponse,
  GenerateCapsuleResponse,
  RenameCapsuleBody,
  RenameCapsuleResponse,
  PlaygroundCapsuleMeta,
  PlaygroundCapsulesResponse,
} from './types'
import { PLAYGROUND_PAGE_SIZE_MAX } from './wardrobe'

export const getPlaygroundCapsules = async (pageNum = 1, pageSize = PLAYGROUND_PAGE_SIZE_MAX) => {
  return apiClient.get<PlaygroundCapsulesResponse>('/playground/capsules', {
    params: { page_num: pageNum, page_size: pageSize },
  })
}

export const createPlaygroundCapsule = async (body: CreateCapsuleBody) => {
  return apiClient.post<CreateCapsuleResponse>('/playground/capsules', body)
}

export const deletePlaygroundCapsule = async (capsuleId: number) => {
  return apiClient.delete<DeleteCapsuleResponse>(`/playground/capsules/${capsuleId}`)
}

/** Header only — looks and inventory are their own paginated calls (§2.2). */
export const getPlaygroundCapsule = async (capsuleId: number) => {
  return apiClient.get<PlaygroundCapsuleMeta>(`/playground/capsules/${capsuleId}`)
}

export const getPlaygroundCapsuleOutfits = async (
  capsuleId: number,
  pageNum = 1,
  pageSize = PLAYGROUND_PAGE_SIZE_MAX,
) => {
  return apiClient.get<CapsuleOutfitsResponse>(`/playground/capsules/${capsuleId}/outfits`, {
    params: { page_num: pageNum, page_size: pageSize },
  })
}

export const getPlaygroundCapsuleItems = async (
  capsuleId: number,
  pageNum = 1,
  pageSize = PLAYGROUND_PAGE_SIZE_MAX,
) => {
  return apiClient.get<CapsuleInventoryResponse>(`/playground/capsules/${capsuleId}/items`, {
    params: { page_num: pageNum, page_size: pageSize },
  })
}

export const addPlaygroundCapsuleItems = async (capsuleId: number, body: AddCapsuleItemsBody) => {
  return apiClient.post<AddCapsuleItemsResponse>(`/playground/capsules/${capsuleId}/items`, body)
}

export const getCapsuleItemRemovalImpact = async (capsuleId: number, capsuleItemId: number) => {
  return apiClient.get<CapsuleItemRemovalImpact>(
    `/playground/capsules/${capsuleId}/items/${capsuleItemId}/removal-impact`,
  )
}

export const deletePlaygroundCapsuleItem = async (capsuleId: number, capsuleItemId: number) => {
  return apiClient.delete<DeleteCapsuleItemResponse>(
    `/playground/capsules/${capsuleId}/items/${capsuleItemId}`,
  )
}

export const detachOutfitsFromCapsule = async (capsuleId: number, outfitIds: number[]) => {
  return apiClient.delete<DetachOutfitsResponse>(`/playground/capsules/${capsuleId}/outfits`, {
    data: { outfitIds },
  })
}

/** One more ML-generated look for an owned capsule; 400 for a wardrobe-only capsule, 503 when the model is down. */
export const generatePlaygroundCapsule = async (capsuleId: number) => {
  return apiClient.post<GenerateCapsuleResponse>(`/playground/capsules/${capsuleId}/generate`)
}

export const renamePlaygroundCapsule = async (capsuleId: number, body: RenameCapsuleBody) => {
  return apiClient.patch<RenameCapsuleResponse>(`/playground/capsules/${capsuleId}`, body)
}
